import { formatDateTime } from '@belong/common';
import { Text } from '@belong/ui';
import type { GroupedBalancesInfoModel } from 'api/models.v2';

type PayInAdvanceBannerProps = {
  advanceBalances: GroupedBalancesInfoModel[];
};

function formatAdvanceBalanceDescriptions(
  advanceBalances: GroupedBalancesInfoModel[],
) {
  const descriptions = advanceBalances.map((balance) => balance.description);

  if (descriptions.length === 0) return [];
  if (descriptions.length === 1) return [descriptions[0]];
  if (descriptions.length === 2) {
    return [descriptions[0], ' and ', descriptions[1]];
  }

  return [
    descriptions.slice(0, -1).join(', '),
    ', and ',
    descriptions[descriptions.length - 1],
  ];
}

export function PayInAdvanceBanner({
  advanceBalances,
}: PayInAdvanceBannerProps) {
  if (advanceBalances.length === 0) {
    return null;
  }

  const balanceParts = formatAdvanceBalanceDescriptions(advanceBalances);
  const isSingle = advanceBalances.length === 1;
  const dueOn = isSingle ? advanceBalances[0]?.dueOn : undefined;
  const formattedDueDate = dueOn
    ? formatDateTime({ dateTime: dueOn, format: 'MMMM do' })
    : undefined;

  return (
    <div className="rounded-small border border-solid border-red bg-red-translucent-light p-xs sm:p-sm">
      <Text className="mb-xs" fontWeight="semibold" variant="h3">
        Paying in advance?
      </Text>
      <Text>
        {balanceParts.map((part, index) =>
          index % 2 === 0 ? (
            <span key={index} className="font-semibold">
              {part}
            </span>
          ) : (
            part
          ),
        )}{' '}
        {isSingle && formattedDueDate
          ? `is not due until ${formattedDueDate}.`
          : `${advanceBalances.length > 1 ? 'are' : 'is'} not due yet.`}{' '}
        Would you like to proceed?
      </Text>
    </div>
  );
}
